import React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMunicipalityLocalities } from '../hooks/useMunicipalityLocalities';
import { usePreRegistrationStatuses } from '../hooks/usePreRegistrationQueries';
import { EMPTY_ACCOMMODATION_FILTERS } from '../hooks/usePreRegisteredAccommodationSearch';
import type { AccommodationFilterValues } from '../types/pre-registration.types';
import { ALL, fromSelectValue, toSelectValue } from '../utils/select.utils';
import { MunicipalitySelect } from './MunicipalitySelect';

interface PreRegisteredAccommodationsFiltersProps {
  values: AccommodationFilterValues;
  onChange: (values: AccommodationFilterValues) => void;
}

export const PreRegisteredAccommodationsFilters: React.FC<PreRegisteredAccommodationsFiltersProps> = ({
  values,
  onChange,
}) => {
  const { data: localities = [] } = useMunicipalityLocalities(values.municipalityId);
  const { data: statuses = [] } = usePreRegistrationStatuses();

  const hasFilters =
    values.search !== '' ||
    values.municipalityId !== undefined ||
    values.localityId !== undefined ||
    values.statusId !== undefined ||
    values.includeDeleted;

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={values.search}
          onChange={(event) => onChange({ ...values, search: event.target.value })}
          placeholder="Buscar por denominación, referente o dirección"
          className="pl-9"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="municipality">Municipio</Label>
          <MunicipalitySelect
            id="municipality"
            value={values.municipalityId}
            onChange={(municipalityId) =>
              // A new municipality invalidates the selected locality
              onChange({ ...values, municipalityId, localityId: undefined })
            }
          />
        </div>

        <div className="space-y-1">
          <Label>Localidad</Label>
          <Select
            value={toSelectValue(values.localityId)}
            onValueChange={(value) => onChange({ ...values, localityId: fromSelectValue(value) })}
            disabled={values.municipalityId === undefined || localities.length === 0}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={values.municipalityId === undefined ? 'Elegí un municipio' : 'Todas'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas</SelectItem>
              {localities.map((locality) => (
                <SelectItem key={locality.id} value={String(locality.id)}>
                  {locality.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Estado</Label>
          <Select
            value={toSelectValue(values.statusId)}
            onValueChange={(value) => onChange({ ...values, statusId: fromSelectValue(value) })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos (sin eliminados)</SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status.id} value={String(status.id)}>
                  {status.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        {/* The backend ignores includeDeleted when a status is selected */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="include-deleted"
            checked={values.includeDeleted}
            disabled={values.statusId !== undefined}
            onCheckedChange={(checked) => onChange({ ...values, includeDeleted: checked === true })}
          />
          <Label htmlFor="include-deleted" className="font-normal">
            Mostrar eliminados
          </Label>
        </div>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_ACCOMMODATION_FILTERS)}>
            <X className="mr-1 h-4 w-4" />
            Limpiar filtros
          </Button>
        )}
      </div>
    </div>
  );
};
